import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getBulkDropdownData } from "../../api/keyvalue";
import { Codesandbox, Search, AlertCircle } from "react-feather";
import { FORMS, MODULES } from "../../const";
import Button from "../../components/input/Button";
import LoadingSpinner from "../../components/LoadingSpinner";
import { Select } from "semantic-ui-react";
import Grid from "../../components/Grid";
import {
  ActiveCellStyle,
  StatusCellStyle,
} from "../../components/renderer/StyleRenderer";
import useFirstAndLastAuthorizedTab from "../../hooks/useFirstAndLastAuthorizedTab";
import useUserCompany from "../../hooks/useUserCompany";
import SideDrawer from "../../components/input/SideDrawer";
import Tab from "../../components/Tab";
import BankAddComponent from "../../components/master/BankAddComponent";
import {
  createBank,
  updateBank,
  searchBanks,
  getBankById,
} from "../../api/bankApi";
import { errorActions } from "../../store/error-slice";
import { toast } from "react-toastify";
import Modal from "../../components/Modal";
import { getCompanies } from "../../api/companyApi";

const FORM = FORMS.BANK.BANK_MASTER.form;

const Bank = () => {
  const user = useSelector((state) => state.user);
  const userCompany = useUserCompany();
  const { firstViableTab } = useFirstAndLastAuthorizedTab({
    module: MODULES.Master,
    section: FORMS.BANK,
  });

  const [tab, setTab] = useState(1);
  const [drawerTab, setDrawerTab] = useState(1);
  const [status, setStatus] = useState(null);
  const [active, setActive] = useState(null);
  const dispatch = useDispatch();
  const errorState = useSelector(
    (state) => state.error.bankMasterErrors || {},
  );

  // Search criteria states
  const [searchCode, setSearchCode] = useState("");
  const [searchName, setSearchName] = useState("");

  const [editMode, setEditMode] = useState(false);
  const [searchFetchLoading, setSearchFetchLoading] = useState(false);
  const [searchSubmitLoading, setSearchSubmitLoading] = useState(false);
  const [searchRes, setSearchRes] = useState([]);

  const [editBank, setEditBank] = useState(null);
  const [activeAccordion1, setActiveAccordion1] = useState(true);
  const [resetVisible, setResetVisible] = useState(false);
  const [searchClicked, setSearchClicked] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);

  // dropdown
  const [activeOptions, setActiveOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);

  const bankRef = useRef();

  const deleteWarningRef = useRef();
  const [showDeleteWarning, setShowDeleteWarning] = useState(false);
  const [pendingSubmitEvent, setPendingSubmitEvent] = useState(null);

  const [initialActive, setInitialActive] = useState(1);

  useEffect(() => {
    if (editMode && editBank) {
      setInitialActive(editBank.bnkActive);
    }
  }, [editMode, editBank]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        const { active, status } = await getBulkDropdownData([
          { key: "active" },
          { key: "status" },
        ]);

        setActiveOptions(active || []);
        setStatusOptions(status || []);
        
        try {
          const companies = await getCompanies();
          if (companies) {
             setCompanyOptions(companies);
          }
        } catch(err) {
          console.error("Failed to load companies", err);
        }
      } catch (e) {
         console.error(e);
      }

      setIsLoading(false);
    })();
  }, []);

  const handleDrawerOpen = () => {
    setIsLoading(false);
    setIsDrawerOpen(true);
    setEditMode(false);
    setEditBank(null);
    setDrawerTab(1);
    dispatch(errorActions.clearError({ form: "bankMasterErrors" }));
  };

  const handleDrawerClose = () => {
    setIsDrawerOpen(false);
  };

  const reset = () => {
    if (bankRef.current) bankRef.current.resetForm();
    setEditMode(false);
    setEditBank(null);
  };

  const handleSearchSubmit = async (e) => {
    if (e) e.preventDefault();
    setSearchSubmitLoading(true);
    setSearchClicked(true);
    setActiveAccordion1(false);
    setResetVisible(true);

    try {
      const userCompanyId = userCompany?.comId || (user?.currentCompany ? Number(user.currentCompany) : null);
      const results =
        (await searchBanks({
          bnkCode: searchCode,
          bnkName: searchName,
          bnkCompany: userCompanyId,
          bnkStatus: status,
          bnkActive: active,
        })) || [];

      const mappedResults = results.map((item) => {
        const statusOpt = statusOptions.find(
          (o) =>
            Number(o.value) === Number(item.bnkStatus) ||
            o.key === String(item.bnkStatus),
        );
        const activeOpt = activeOptions.find(
          (o) =>
            Number(o.value) === Number(item.bnkActive) ||
            o.key === String(item.bnkActive),
        );
        const companyOpt = companyOptions.find(
          (o) =>
            Number(o.value) === Number(item.bnkCompany) ||
            o.key === String(item.bnkCompany),
        );
        return {
          ...item,
          statusText: statusOpt ? statusOpt.text : "Pending",
          activeText: activeOpt ? activeOpt.text : "Active",
          companyText: companyOpt ? companyOpt.text : "",
        };
      });

      setSearchRes(mappedResults);
    } catch (error) {
      console.error(error);
      toast.error("Failed to search banks", { containerId: "Main" });
    } finally {
      setSearchSubmitLoading(false);
    }
  };

  const handleSearchReset = (e) => {
    if (e) e.preventDefault();
    setSearchCode("");
    setSearchName("");
    setStatus("");
    setActive("");
    setSearchRes([]);
    setSearchClicked(false);
    setActiveAccordion1(true);
    setResetVisible(false);
  };

  const handleSubmit = async (e, bypassDeleteCheck) => {
    e.preventDefault();

    const data = bankRef.current.getData();
    const isDeletedActive =
      Number(data.bnkActive) === 2 && Number(initialActive) !== 2;

    if (!bypassDeleteCheck && isDeletedActive) {
      setPendingSubmitEvent(e);
      setShowDeleteWarning(true);
      if (deleteWarningRef.current) deleteWarningRef.current.showModal();
      return;
    }

    setIsSubmitLoading(true);
    dispatch(errorActions.clearError({ form: "bankMasterErrors" }));
    const bankErrors = bankRef.current.validateForm();

    if (Object.keys(bankErrors).length > 0) {
      dispatch(
        errorActions.addErrorObject({
          form: "bankMasterErrors",
          errors: bankErrors,
        }),
      );
      setIsSubmitLoading(false);
      toast.error("Please fill all the required fields", {
        autoClose: 5000,
        containerId: "Main",
      });
    } else {
      try {
        if (editMode) {
          await updateBank({
            ...data,
            author: user?.username,
          });
          toast.success("Bank updated successfully!", {
            containerId: "Main",
            autoClose: 2000,
          });
        } else {
          await createBank({
            ...data,
            author: user?.username,
          });
          toast.success("Bank created successfully!", {
            containerId: "Main",
            autoClose: 2000,
          });
        }
        handleDrawerClose();
        reset();
        handleSearchSubmit();
      } catch (error) {
        console.error(error);
        if (error.response && error.response.status === 409) {
          const msg =
            typeof error.response.data === "string" && error.response.data
              ? error.response.data
              : "Bank with this name already exists for the selected company";
          dispatch(
            errorActions.addErrorObject({
              form: "bankMasterErrors",
              errors: {
                bnkName: msg,
              },
            }),
          );
          toast.error(msg, { containerId: "Main" });
        } else if (error.response && error.response.status === 400) {
          const msg =
            typeof error.response.data === "string" && error.response.data
              ? error.response.data
              : "Invalid Bank Name format";
          dispatch(
            errorActions.addErrorObject({
              form: "bankMasterErrors",
              errors: {
                bnkName: msg,
              },
            }),
          );
          toast.error(msg, { containerId: "Main" });
        } else {
          toast.error("An error occurred while saving", { containerId: "Main" });
        }
      } finally {
        setIsSubmitLoading(false);
      }
    }
  };

  const deleteWarningModal = (
    <div style={{ padding: "20px" }}>
      <h3 style={{ display: "flex", alignItems: "center", gap: "5px" }}>
        <AlertCircle color="red" size="25" />
        <p>Warning</p>
      </h3>
      <p>Active is set to Deleted. Are you sure you want to proceed?</p>
      <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
        <Button
          label="Cancel"
          onClick={() => {
            if (deleteWarningRef.current) deleteWarningRef.current.close();
            setShowDeleteWarning(false);
            setPendingSubmitEvent(null);
          }}
        />
        <Button
          label="Proceed"
          className="ui red button"
          onClick={() => {
            if (deleteWarningRef.current) deleteWarningRef.current.close();
            setShowDeleteWarning(false);
            handleSubmit(pendingSubmitEvent, true);
            setPendingSubmitEvent(null);
          }}
        />
      </div>
    </div>
  );

  return (
    <>
      <div
        className="ui attached tabular"
        style={{
          display: "flex",
          justifyContent: "space-around",
        }}
      >
        <div className="ui tabular top attached menu">
          <div
            className={`item ${tab === 1 ? "active" : ""}`}
            onClick={() => {
              setTab(1);
            }}
          >
            <span>
              <Codesandbox size={15} style={{ marginRight: "5px" }} />
            </span>
            Bank Search
          </div>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: "0px",
          }}
          className="ui tabular menu top attached"
        >
          <Button
            template="drawerOpen"
            disabled={firstViableTab === -1}
            onClick={handleDrawerOpen}
            authenticated={true}
            form={FORM}
            module={MODULES.Master}
            editMode={false}
          />
        </div>
      </div>

      <div
        className="ui bottom attached active tab segment"
        style={{ display: tab === 1 ? "block" : "none" }}
      >
        <div className="ui styled accordion" style={{ width: "100%" }}>
          <div
            className={`title ${activeAccordion1 ? "active" : ""}`}
            onClick={() => setActiveAccordion1(!activeAccordion1)}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <span>Search</span>
                <Search size={14} style={{ marginLeft: "10px" }} />
                <i className="dropdown icon"></i>
              </div>

              <i
                className="refresh icon primary button"
                style={{
                  display: `${resetVisible ? "flex" : "none"}`,
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  handleSearchReset(e);
                }}
              ></i>
            </div>
          </div>
          <div className={`content ${activeAccordion1 ? "active" : ""}`}>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <form className="ui form" onSubmit={handleSearchSubmit}>
                <h4 className="ui dividing header">Bank Information</h4>
                <div className="two fields">
                  <div className="field">
                    <label>Bank Code</label>
                    <input
                      type="text"
                      placeholder="Bank Code"
                      name="bnkCode"
                      value={searchCode}
                      onChange={(e) => setSearchCode(e.target.value)}
                    />
                  </div>
                  <div className="field">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      placeholder="Bank Name"
                      name="bnkName"
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                    />
                  </div>
                </div>
                
                <div className="two fields">
                  <div className="field">
                    <label>Status</label>
                    <Select
                      options={statusOptions}
                      placeholder="Status"
                      style={{ height: "20px" }}
                      value={status}
                      onChange={(e, data) => {
                        setStatus(data.value);
                      }}
                    />
                  </div>

                  <div className="field">
                    <label>Active</label>
                    <Select
                      options={activeOptions}
                      placeholder="Active"
                      style={{ height: "20px" }}
                      value={active}
                      onChange={(e, data) => {
                        setActive(data.value);
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  <div
                    className="field"
                    style={{
                      width: "100%",
                      display: "flex",
                      justifyContent: "flex-end",
                    }}
                  >
                    <Button
                      template="searchReset"
                      onClick={(e) => {
                        handleSearchReset(e);
                      }}
                    />
                    <Button template="search" onClick={handleSearchSubmit} />
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>

        {searchClicked && (
          <>
            {searchSubmitLoading ? (
              <LoadingSpinner />
            ) : (
              <Grid
                isLoading={searchFetchLoading}
                content={searchRes}
                config={[
                  {
                    headerName: "Id",
                    field: "bnkId",
                    sort: "desc",
                    hide: true,
                  },
                  {
                    headerName: "Bank Code",
                    field: "bnkCode",
                  },
                  {
                    headerName: "Bank Name",
                    field: "bnkName",
                  },
                  {
                    headerName: "Description",
                    field: "bnkDescription",
                  },
                  {
                    headerName: "Company",
                    field: "companyText",
                  },
                  {
                    headerName: "Status",
                    field: "statusText",
                    cellRenderer: StatusCellStyle,
                  },
                  {
                    headerName: "Active",
                    field: "activeText",
                    cellRenderer: ActiveCellStyle,
                  },
                ]}
                onCellDoubleClicked={async (props) => {
                  setSearchFetchLoading(true);
                  try {
                    const response = await getBankById(props.data.bnkId);
                    if (response) {
                      setEditBank(response);
                      setEditMode(true);
                      setIsDrawerOpen(true);
                    }
                  } catch (error) {
                    console.error(error);
                    toast.error("Failed to load bank details", {
                      containerId: "Main",
                    });
                  } finally {
                    setSearchFetchLoading(false);
                  }
                }}
              />
            )}
          </>
        )}
      </div>

      <SideDrawer
        open={isDrawerOpen}
        handleDrawerClose={handleDrawerClose}
        head={
          editMode ? (
            <div
              className="bank-code-header"
              style={{ padding: "10px 16px", borderBottom: "1px solid #ccc" }}
            >
              <h3>Bank: {editBank?.bnkName}</h3>
            </div>
          ) : (
            <h3>Create Bank</h3>
          )
        }
        body={
          <>
            <form className="drawer-content" onSubmit={handleSubmit}>
              <div style={{ width: "60vw", boxShadow: "none" }}>
                <div className="ui top attached tabular menu">
                  <Tab
                    Icon={Codesandbox}
                    onClick={() => {
                      setDrawerTab(1);
                    }}
                    active={drawerTab === 1}
                    errors={
                      Object.keys(errorState.bankMasterErrors || {})
                        .length > 0
                    }
                    label="Bank Details"
                    authenticated={true}
                    form={FORM}
                    showMandatory={true}
                    isMandatory={true}
                  />
                </div>
                <div
                  className="ui bottom attached active tab segment"
                  style={{ display: drawerTab === 1 ? "block" : "none" }}
                >
                  <BankAddComponent
                    ref={bankRef}
                    isLoading={isLoading}
                    errors={errorState.bankMasterErrors || errorState}
                    dropdown={{
                      statusOptions,
                      activeOptions,
                      companyOptions,
                    }}
                    editMode={editMode}
                    currentValues={editMode ? editBank : null}
                  />
                </div>
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                    padding: "10px 0",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <Button
                      type="submit"
                      template="create"
                      isLoading={isSubmitLoading}
                      create={!editMode}
                      form={FORM}
                      module={MODULES.Master}
                      authenticated={true}
                      editMode={editMode}
                    />
                  </div>
                </div>
              </div>
            </form>
            <Modal
              ref={deleteWarningRef}
              closeButton={false}
              minWidth={"300px"}
            >
              {showDeleteWarning && deleteWarningModal}
            </Modal>
          </>
        }
        style={{ width: "60vw", boxShadow: "none" }}
      />
    </>
  );
};

export default Bank;
